import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format, formatDistanceToNow } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import {
  collection,
  doc,
  getDocs,
  limit as limitar,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiBriefcase, FiClock, FiMail, FiPhone, FiSearch, FiUser } from 'react-icons/fi';
import MarcaBeta from '~/components/MarcaBeta';
import firebaseFirestore from '~/config/firebase/firestore';
import { useDirectorioClientes } from './directorioClientes';
import { buscarParecidos, soloHayParecidos } from './parecidos';

/**
 * Buscar a alguien que ya reservó antes, para no volver a teclear sus datos.
 *
 * Solo aparece dentro del panel. Firestore no sabe buscar texto suelto, así
 * que la búsqueda se hace sobre una libreta que el servidor mantiene al día
 * —una ficha por persona— y que el panel se baja de una vez. Con las 2.455
 * fichas en memoria, buscar por parecido no cuesta ni una consulta.
 *
 * Si esa libreta no existiera (una empresa recién creada, o un fallo), el
 * buscador no se queda mudo: vuelve a lo de antes y mira las últimas
 * reservas. Peor, pero nunca roto.
 */

/** Cuántas reservas se miran cuando hay que tirar del respaldo. */
const RESERVAS_A_MIRAR = 400;

/** Resultados en pantalla. Más no se leen; se afina la búsqueda. */
const MAXIMO_RESULTADOS = 40;

export type FichaCliente = {
  clave: string;
  nombre: string;
  empresa?: string;
  correo?: string;
  documento?: string;
  telefono?: string;
  direccion?: string;
  /** Cualquier otro campo del formulario que traía la reserva. */
  extra: Record<string, unknown>;
  ultima?: Date;
  cuantas: number;
};

const CAMPOS_PROPIOS = new Set([
  'id', 'code', 'location', 'status', 'statusHistory', 'payment', 'recurrence',
  'recurrenceTotal', 'recurrenceResumen', 'recurrenceFechas', 'recurrenceFechasEn',
  'datetime', 'startDatetime', 'endDatetime', 'startDate', 'endDate', 'date',
  'startHour', 'endHour', 'time', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
  'cancelledAt', 'cancelledBy', 'cancelledByUser', 'isUpdatedByUser', 'from', 'source',
  'lang', 'externalId', 'company', 'numberPeople', '__codigoPendiente',
  // Igual que en el servidor: estos ya se asignan uno a uno al rellenar.
  'email', 'emailConfirmation', 'namesAndSurnames', 'name', 'companyName',
  'identificationNumber', 'phone', 'phoneNumber', 'billingAddress',
]);

/** Una reserva → ficha de la persona que la hizo. Solo para el respaldo. */
const aFicha = (r: Record<string, any>): FichaCliente | null => {
  const correo = String(r.email ?? '').trim().toLowerCase();
  const documento = String(r.identificationNumber ?? '').trim();
  const nombre = String(r.namesAndSurnames ?? r.name ?? '').trim();
  const clave = correo || documento || nombre.toLowerCase();
  if (!clave) return null;

  const extra: Record<string, unknown> = {};
  Object.entries(r).forEach(([k, v]) => {
    if (!CAMPOS_PROPIOS.has(k) && typeof v !== 'object') extra[k] = v;
  });

  return {
    clave,
    nombre: nombre || correo || documento,
    empresa: r.companyName ? String(r.companyName) : undefined,
    correo: correo || undefined,
    documento: documento || undefined,
    telefono: r.phone ? String(r.phone) : undefined,
    direccion: r.billingAddress ? String(r.billingAddress) : undefined,
    extra,
    ultima: r.startDatetime?.toDate?.() ?? r.datetime?.toDate?.() ?? undefined,
    cuantas: 1,
  };
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  onElegir: (ficha: FichaCliente) => void;
};

const BuscarCliente = ({ isOpen, onClose, companyId, onElegir }: Props) => {
  const [texto, setTexto] = useState('');

  // La suscripción se abre en cuanto la modal se usa por primera vez y se
  // queda viva el resto de la sesión: reabrirla no cuesta ninguna lectura.
  const usado = useRef(false);
  if (isOpen) usado.current = true;
  const directorio = useDirectorioClientes(companyId, usado.current);

  const hayDirectorio = directorio.estado === 'listo' && directorio.fichas.length > 0;
  // Un directorio vacío cuenta como no tenerlo. Si la empresa de verdad no ha
  // tenido clientes, el respaldo tampoco encontrará nada y no se pierde nada;
  // pero si el directorio se quedó a medias, el buscador sigue sirviendo en
  // vez de decir que no hay nadie.
  const necesitaRespaldo =
    directorio.estado === 'ausente' ||
    directorio.estado === 'error' ||
    (directorio.estado === 'listo' && directorio.fichas.length === 0);

  // ── Respaldo: las últimas reservas, como se hacía antes ──────────────
  const [respaldo, setRespaldo] = useState<FichaCliente[]>([]);
  const [cargandoRespaldo, setCargandoRespaldo] = useState(false);
  const respaldoPedido = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setTexto('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !necesitaRespaldo || respaldoPedido.current || !companyId) return;
    respaldoPedido.current = true;

    let vivo = true;
    (async () => {
      setCargandoRespaldo(true);
      try {
        const snap = await getDocs(
          query(
            collection(firebaseFirestore, 'reservations'),
            where('location.company', '==', doc(firebaseFirestore, 'companies', companyId)),
            orderBy('createdAt', 'desc'),
            limitar(RESERVAS_A_MIRAR)
          )
        );
        if (!vivo) return;

        const porClave = new Map<string, FichaCliente>();
        snap.docs.forEach((d) => {
          const ficha = aFicha(d.data() as Record<string, any>);
          if (!ficha) return;
          const previa = porClave.get(ficha.clave);
          if (previa) previa.cuantas += 1;
          else porClave.set(ficha.clave, ficha);
        });
        setRespaldo([...porClave.values()]);
      } finally {
        if (vivo) setCargandoRespaldo(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [isOpen, necesitaRespaldo, companyId]);

  const fichas = hayDirectorio ? directorio.fichas : respaldo;
  const cargando = necesitaRespaldo
    ? cargandoRespaldo
    : directorio.estado === 'cargando';

  const resultados = useMemo(
    () => buscarParecidos(fichas, texto, MAXIMO_RESULTADOS),
    [fichas, texto]
  );

  const aproximados = useMemo(
    () => soloHayParecidos(resultados, texto),
    [resultados, texto]
  );

  const procedencia = hayDirectorio
    ? `${directorio.fichas.length.toLocaleString('es')} clientes${
        directorio.actualizado
          ? ` · al día de hace ${formatDistanceToNow(directorio.actualizado, {
              locale: esLocale,
            })}`
          : ''
      }`
    : necesitaRespaldo
    ? `Entre las ${RESERVAS_A_MIRAR} reservas más recientes`
    : 'Preparando el directorio de clientes…';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent>
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={FiSearch} color='blue.500' />
            <Text fontSize='md'>Buscar un cliente</Text>
            <MarcaBeta />
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            De quien ya reservó antes. Al elegirlo se rellenan sus datos.
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          <InputGroup mb={2}>
            <InputLeftElement pointerEvents='none'>
              <Icon as={FiSearch} color='gray.400' />
            </InputLeftElement>
            <Input
              autoFocus
              placeholder='Nombre, empresa, correo, cédula o celular'
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              borderRadius='md'
            />
          </InputGroup>

          <Text fontSize='xs' color='gray.400' mb={3}>
            {procedencia}
          </Text>

          {cargando ? (
            <VStack align='stretch' spacing={2}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} h='62px' borderRadius='md' />
              ))}
            </VStack>
          ) : resultados.length === 0 ? (
            <Box py={8} textAlign='center'>
              <Text fontSize='sm' color='gray.600'>
                {texto
                  ? `Nadie coincide con "${texto}".`
                  : 'Todavía no hay reservas de las que sacar clientes.'}
              </Text>
              {texto && necesitaRespaldo && (
                <Text fontSize='xs' color='gray.500' mt={2}>
                  Se buscan las {RESERVAS_A_MIRAR} reservas más recientes. Si el cliente hace
                  mucho que no viene, escribe sus datos a mano.
                </Text>
              )}
            </Box>
          ) : (
            <VStack align='stretch' spacing={2}>
              {aproximados && (
                <Text fontSize='xs' color='orange.600'>
                  Ninguno coincide exactamente. Esto es lo más parecido.
                </Text>
              )}
              {resultados.map(({ ficha: f }) => (
                <Flex
                  key={f.clave}
                  onClick={() => {
                    onElegir(f);
                    onClose();
                  }}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onElegir(f);
                      onClose();
                    }
                  }}
                  direction='column'
                  px={3}
                  py={2}
                  borderWidth='1px'
                  borderColor='gray.200'
                  borderRadius='md'
                  cursor='pointer'
                  _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                  transition='all .12s'
                >
                  <HStack justify='space-between' align='flex-start'>
                    <HStack spacing={2} minW={0}>
                      <Icon as={FiUser} color='gray.400' boxSize={3.5} flexShrink={0} />
                      <Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
                        {f.nombre}
                      </Text>
                    </HStack>
                    {f.cuantas > 1 && (
                      <Badge colorScheme='blue' fontSize='0.6em' flexShrink={0}>
                        {f.cuantas} reservas
                      </Badge>
                    )}
                  </HStack>

                  <HStack spacing={4} mt={1} fontSize='xs' color='gray.600' wrap='wrap'>
                    {f.empresa && (
                      <HStack spacing={1}>
                        <Icon as={FiBriefcase} boxSize={3} />
                        <Text noOfLines={1}>{f.empresa}</Text>
                      </HStack>
                    )}
                    {f.correo && (
                      <HStack spacing={1}>
                        <Icon as={FiMail} boxSize={3} />
                        <Text noOfLines={1}>{f.correo}</Text>
                      </HStack>
                    )}
                    {f.telefono && (
                      <HStack spacing={1}>
                        <Icon as={FiPhone} boxSize={3} />
                        <Text noOfLines={1}>{f.telefono}</Text>
                      </HStack>
                    )}
                    {f.ultima && (
                      <HStack spacing={1} color='gray.500'>
                        <Icon as={FiClock} boxSize={3} />
                        <Text noOfLines={1}>
                          {format(f.ultima, 'd MMM yyyy', { locale: esLocale })}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BuscarCliente;
